<template>
  <div class="catalog-options">
    <b-form-group>
      <label class="font-weight-bold">{{ $t("File") }}</label>
      <b-input-group v-tooltip="!serviceFilePath ? $t('NoFileSelected') : false">
        <b-form-file :class="{ 'border-danger': !serviceFilePath }" :placeholder="file ? file.name : ''" @change="uploadFile" />
      </b-input-group>
    </b-form-group>
    <p class="options-headline">{{ $t("FileFormat") }}</p>
    <div class="row">
      <div class="col option-left">
        <b-form-group>
          <label class="font-weight-bold">{{ $t("FieldSeparator") }}</label>
          <b-input v-tooltip="separatorError" v-model="fieldSeparator" @change="getFieldHeaders" :class="{ 'border-danger': !!separatorError }" />
        </b-form-group>
      </div>
      <div class="col option-right">
        <b-form-group>
          <label class="font-weight-bold">{{ $t("FieldDelimiter") }}</label>
          <b-input v-tooltip="delimiterError" v-model="fieldDelimiter" @change="getFieldHeaders" :class="{ 'border-danger': !!delimiterError }" />
        </b-form-group>
      </div>
    </div>
    <b-checkbox class="mb-3" v-model="isFirstLineHeader" @change="changeIsFirstLine">{{ $t("FirstRowContainsFieldName") }}</b-checkbox>
    <div class="row">
      <div class="col dropdown-left">
        <label class="font-weight-bold">{{ $t("TargetField") }}</label>
        <dropdown v-model="artNrType" :items="artNrTypeFields" value-field="value" text-field="text" @input="getFieldHeaders">
          <template #item-template="{ entry }">
            <span v-if="entry"> {{ entry.text }} </span>
          </template>
        </dropdown>
      </div>
      <div class="col dropdown-right">
        <label class="font-weight-bold">{{ $t("SourceFileField") }}</label>
        <dropdown v-model="artNrSource" :items="artNrSourceFields" value-field="value" text-field="text" @vnode-updated="validate" :disabled="!serviceFilePath">
          <template #item-template="{ entry }">
            <span v-if="entry"> {{ entry.text }} </span>
          </template>
        </dropdown>
      </div>
    </div>
    <div class="catalog-modal-buttons">
      <b-btn class="mr-2" @click="stepChange('back')">{{ $t("Back") }}</b-btn>
      <b-btn @click="stepChange('continue')" :disabled="!validation">{{ $t("Continue") }}</b-btn>
    </div>
  </div>
</template>

<script>
import Dropdown from "../../../../common/components/Dropdown.vue";
import articleNumberTypesApi from "../../../../services/articleNumberTypes";
import channelCreatorApi from "../../../../services/channelCreator";
import fieldHeadersFromSourceFileApi from "../../../../services/fieldHeadersFromSourceFile";
import { useToast } from "@common/composable/toast";
import { useSanitize } from "@common/composable/useSanitize";

export default {
  setup() {
    const { createToast } = useToast();
    const { sanitizeFilename } = useSanitize();
    return { createToast, sanitizeFilename };
  },
  components: { Dropdown },
  props: ["filePassed", "fileDataPassed", "fields"],
  emits: ["stepChange"],

  data() {
    return {
      file: this.filePassed ? this.filePassed : null,
      serviceFilePath: this.fileDataPassed ? this.fileDataPassed.pathToCsvFile : null,
      fieldSeparator: this.fileDataPassed ? this.fileDataPassed.separator : ";",
      fieldDelimiter: this.fileDataPassed ? this.fileDataPassed.quoteChar : '"',
      isFirstLineHeader: this.fileDataPassed ? this.fileDataPassed.isFirstLineHeader : false,
      artNrType: this.fields ? this.fields.artNrTypeSelected : 0,
      artNrSource: this.fields ? this.fields.artNrSourceSelected : null,
      artNrTypesFetched: [],
      artNrSourcesFetched: [],
      validation: false,
      canGetFieldHeaders: false,
      fileError: "",
      separatorError: "",
      delimiterError: ""
    };
  },

  mounted() {
    this.getArtNrTypes();
    this.getFieldHeaders();
  },

  computed: {
    artNrTypeFields() {
      return this.artNrTypesFetched.map((x, index) => ({
        text: x.name,
        value: index
      }));
    },

    artNrSourceFields() {
      if (this.artNrSourcesFetched.length > 0)
        return this.artNrSourcesFetched.map((x, index) => ({
          text: x,
          value: index
        }));
      else return [];
    }
  },

  methods: {
    //emit data to preserve state
    stepChange(direction) {
      const artNrTypeName = this.artNrTypesFetched[this.artNrType].name;
      const fileData = {
        pathToCsvFile: this.serviceFilePath,
        isFirstLineHeader: this.isFirstLineHeader,
        separator: this.fieldSeparator,
        quoteChar: this.fieldDelimiter,
        selectedArticleNumberType: {
          articleNumberType: this.checkArticleNrType(artNrTypeName) ? 0 : 1,
          name: artNrTypeName
        },
        selectedHeader: this.artNrSourcesFetched[this.artNrSource]
      };

      this.$emit("stepChange", {
        currentState: "CatalogOptions",
        nextState: direction === "continue" ? "CatalogNaming" : "ExistingCatalog",
        fileData: fileData,
        file: this.file,
        fields: {
          artNrTypeSelected: this.artNrType,
          artNrSourceSelected: this.artNrSource
        }
      });
    },

    // Check if selected ArticleType is default
    checkArticleNrType(selectedArtNrType) {
      let isDefault = false;
      this.artNrTypesFetched.map((artNrType) => {
        if (artNrType.name === selectedArtNrType && artNrType.articleNumberType === "Default") isDefault = true;
      });
      return isDefault;
    },

    validate() {
      //Reset previous validation
      this.fileError = "";
      this.separatorError = "";
      this.delimiterError = "";
      this.artNrTypeError = "";
      this.artNrSourceError = "";
      this.validation = true;
      this.canGetFieldHeaders = true;

      if (!this.file) {
        this.fileError = "File Error";
        this.validation = false;
        this.canGetFieldHeaders = false;
        return;
      }
      if (!this.fieldSeparator) {
        this.separatorError = this.$t("ErrorMessage.Mandatory");
        this.validation = false;
        this.canGetFieldHeaders = false;
        return;
      }

      if (this.artNrSource === null) {
        this.validation = false;
        return;
      }
    },

    changeIsFirstLine() {
      this.artNrSource = null; // So validation is false, and continue disabled
      this.getFieldHeaders();
    },

    async getArtNrTypes() {
      this.artNrTypesFetched = await articleNumberTypesApi.get();
    },

    async getFieldHeaders() {
      this.validate();
      if (this.canGetFieldHeaders !== false) {
        try {
          const result = await fieldHeadersFromSourceFileApi.post({
            pathToCsvFile: this.serviceFilePath,
            isFirstLineHeader: this.isFirstLineHeader,
            separator: this.fieldSeparator,
            quoteChar: this.fieldDelimiter
          });

          this.artNrSourcesFetched = result;
        } catch (exception) {
          if (Object.hasOwn(exception.response.data, "separator")) this.separatorError = this.$t("ErrorMessage.NotConverted");
          if (Object.hasOwn(exception.response.data, "quoteChar")) this.delimiterError = this.$t("ErrorMessage.NotConverted");

          this.validation = false;
          this.canGetFieldHeaders = false;
          this.artNrSourcesFetched = [];
        }
      } else this.artNrSourcesFetched = [];
    },

    async uploadFile(e) {
      const file = e.target.files[0];
      this.file = file;
      if (!file) {
        this.serviceFilePath = null;
        this.getFieldHeaders();
        return;
      }

      const formData = new FormData();
      formData.append("file", file, this.sanitizeFilename(file.name));

      try {
        this.serviceFilePath = await channelCreatorApi.uploadEcfFile("source_files", true, formData);
        this.getFieldHeaders();
      } catch (error) {
        this.serviceFilePath = null;

        let errorMessage = "";
        if (error && error.isAxiosError) {
          const status = error.response?.status;
          const responseData = error.response?.data;

          if (status === 400) {
            errorMessage =
              responseData?.file && this.$te(`api.errors.${responseData.file[0]}`) ? this.$t(`api.errors.${responseData.file[0]}`) : this.$t("UploadFailed");
          } else {
            errorMessage = statusText || this.$t("UploadFailed");
          }
          if (errorMessage) {
            this.errorToast(this.$t("Error"), errorMessage);
          } else {
            console.error("File upload failed: ", error);
          }
        }
      }
    },

    errorToast(title, text) {
      this.createToast({ text, title, variant: "danger", titleIcon: "Channel-Creator", textIllustration: "FileCsv", statusTextIcon: "XCircleSolid" });
    }
  }
};
</script>
